'use client'

import { useState } from 'react'
import { MessageCircle, TrendingDown, Clock, TrendingUp, Headphones, Zap, Shield, Users, Mic } from 'lucide-react'
import ITSMChatWidget from '@/components/itsm/ITSMChatWidget'
import VoiceAgent from '@/components/VoiceAgent'
import Link from 'next/link'

export default function Home() {
  const [showChat, setShowChat] = useState(false)
  const [showVoice, setShowVoice] = useState(false)

  const features = [
    { icon: MessageCircle, title: 'Chat Inteligente', desc: 'Respuestas en español mexicano con contexto técnico' },
    { icon: Headphones, title: 'Soporte de Voz', desc: 'Push-to-talk para reportar problemas sin escribir' },
    { icon: Zap, title: 'Respuesta Inmediata', desc: 'Sin colas de espera, resolución en segundos' },
    { icon: Shield, title: 'Base de Conocimiento', desc: 'KB articles integrados con búsqueda semántica' },
    { icon: Clock, title: 'Disponibilidad 24/7', desc: 'Agente siempre activo para soporte continuo' },
    { icon: Users, title: 'Escalación Humana', desc: 'Transferencia fluida a agentes humanos cuando sea necesario' }
  ]

  const stats = [
    { label: 'Reducción en tiempos de respuesta', value: '-85%', icon: TrendingDown, color: 'text-green-600' },
    { label: 'Disponibilidad continua', value: '24/7', icon: Clock, color: 'text-blue-600' },
    { label: 'Reducción en costos de soporte', value: '-60%', icon: TrendingUp, color: 'text-purple-600' }
  ]

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header con LOGO EVOLBIA */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* LOGO EVOLBIA */}
            <img 
              src="/logos/logo.png" 
              alt="Evolbia" 
              className="h-10 w-auto object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">ITSM Demo</h1>
              <p className="text-xs text-gray-500">Agente de Soporte con IA</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link 
              href="/tickets" 
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
            >
              🎫 Ver Tickets
            </Link>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Demo Activa
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-6">
              <Zap className="w-4 h-4" />
              Powered by Anthropic AI
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Soporte Técnico <span className="text-blue-600">Inteligente</span> y Autónomo
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Agente conversacional que resuelve problemas técnicos, consulta base de conocimiento 
              y crea tickets automáticamente. Implementación en minutos, resultados inmediatos.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => setShowChat(true)} className="btn-primary text-lg px-8 py-3 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Iniciar Demo
              </button>
              <button onClick={() => setShowVoice(true)} className="btn-secondary text-lg px-8 py-3 flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Hablar con Agente
              </button>
              <Link href="/tickets" className="btn-secondary text-lg px-8 py-3 flex items-center gap-2">
                Ver Tickets Creados
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
              <span>✓ Sin registro</span>
              <span>✓ 100% gratuito</span>
              <span>✓ Datos de prueba</span>
            </div>
          </div>
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-bl-full opacity-50"></div>
              <div className="space-y-4 relative">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Agente ITSM</h3>
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      En línea
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                    <p className="text-sm text-gray-700">Hola, soy tu asistente de soporte. ¿En qué puedo ayudarte hoy?</p>
                  </div>
                  <div className="bg-blue-600 text-white rounded-lg p-3 max-w-[80%] ml-auto">
                    <p className="text-sm">No puedo conectarme a la VPN</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                    <p className="text-sm text-gray-700">Entiendo. Vamos a resolverlo en 3 pasos:</p>
                    <ol className="text-sm text-gray-700 mt-2 ml-4 list-decimal space-y-1">
                      <li>Verifica tu conexión a internet</li>
                      <li>Reinicia el cliente VPN</li>
                      <li>Confirma tus credenciales 2FA</li>
                    </ol>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                  <input type="text" placeholder="Escribe tu problema..." className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm" disabled />
                  <button className="bg-blue-600 text-white p-2 rounded-lg">
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Características del Agente</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Diseñado para resolver el 80% de los tickets de soporte técnico de forma autónoma, 
            reduciendo tiempos de respuesta y costos operativos.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="card hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
              <p className="text-gray-600 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 lg:p-12 text-white shadow-lg">
          <h3 className="text-2xl font-bold text-center mb-10">Resultados Comprobados</h3>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-xl p-6 hover:bg-white/20 transition">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <stat.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <p className={`text-4xl lg:text-5xl font-bold mb-2 ${stat.color === 'text-green-600' ? 'text-green-300' : stat.color === 'text-blue-600' ? 'text-blue-200' : 'text-purple-300'}`}>
                  {stat.value}
                </p>
                <p className="text-blue-100 text-sm lg:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chat Widget */}
      <ITSMChatWidget isOpen={showChat} onClose={() => setShowChat(false)} />
      <VoiceAgent isOpen={showVoice} onClose={() => setShowVoice(false)} />

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© 2026 Evolbia - DDC Platform ITSM Demo</p>
          <div className="flex gap-6">
            <Link href="/tickets" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              🎫 Ver Tickets
            </Link>
            <button onClick={() => setShowChat(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              💬 Iniciar Chat
            </button>
          </div>
        </div>
      </footer>
    </main>
  )
}